import { LitElement, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { sheet } from '../styles.js';

export class Textarea extends LitElement {
	static formAssociated = true;
	static styles = [sheet];

	static get properties() {
		return {
			value: { type: String },
			label: { type: String, reflect: true },
			placeholder: { type: String, reflect: true },
			tip: { type: String, reflect: true },
			disabled: { type: Boolean, reflect: true },
			readonly: { type: Boolean, reflect: true },
			error: { type: String }
		};
	}

	#value = '';
	#error = '';
	#initialValue = '';
	#initialTip = '';
	#initialDisabled = false;
	#initialReadonly = false;
	#tooltip = null;

	constructor() {
		super();
		this.internals = this.attachInternals();
		this.label = '';
		this.placeholder = '';
		this.tip = '';
		this.disabled = false;
		this.readonly = false;
	}

	set value(value) {
		const oldValue = this.#value;
		this.#value = value;
		this.requestUpdate('value', oldValue);
		this.internals.setFormValue(value);
		this.dispatchEvent(new CustomEvent('value-changed', { detail: value, bubbles: true, composed: true }));
	}

	get value() {
		return this.#value;
	}

	set error(value) {
		const oldValue = this.#error;
		this.#error = value;
		this.classList.toggle('is-invalid', Boolean(value));
		this.requestUpdate('error', oldValue);
	}
	
	get error() {
		return this.#error;
	}

	connectedCallback() {
		super.connectedCallback();
		this.#initialValue = this.value;
		this.#initialTip = this.tip;
		this.#initialDisabled = this.hasAttribute('disabled');
		this.#initialReadonly = this.hasAttribute('readonly');
		this.internals.setFormValue(this.value);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#tooltip?.dispose();
		this.#tooltip = null;
	}

	formResetCallback() {
		this.value = this.#initialValue;
		this.tip = this.#initialTip;
		this.disabled = this.#initialDisabled;
		this.readonly = this.#initialReadonly;
		this.error = '';
	}

	firstUpdated() {
		const label = this.renderRoot.querySelector('label');
		if (label) {
			this.#tooltip = new bootstrap.Tooltip(label, { container: this.closest('dialog') ?? 'body' });
		}

		this.#updateValueFromLightDOM();
	}

	render() {
		return html`
			<div class="mb-7">
				<div class="form-floating">
					<label>
						${this.label}
						${this.tip ? html`<span class="help-inline ms-1" data-bs-toggle="tooltip" data-bs-original-title="${this.tip}"><i class="icon-fill icon-question"></i></span>` : ''}
					</label>
					<textarea
						name=${this.getAttribute('name') || nothing}
						class="form-control ${classMap({ 'is-invalid': this.error })}"
						placeholder=${this.placeholder}
						.value=${this.value}
						?disabled=${this.disabled}
						?readonly=${this.readonly}
						tabindex=${this.readonly ? '-1' : '0'}
						@focus=${this.#onFocus}
						@input=${this.#onInput}
					></textarea>
					<div class="invalid-feedback lh-1 z-1 position-absolute top-100 start-0 end-0 ${classMap({ 'd-block': this.error })}">${this.error || ''}</div>
				</div>
			</div>
		`;
	}

	focus() {
		if (this.readonly) {
			return;
		}
		this.renderRoot.querySelector('textarea').focus();
	}

	#onFocus(event) {
		if (this.readonly) {
			event.target.blur();
		}
	}

	#onInput(event) {
		this.value = event.target.value;
	}

	#updateValueFromLightDOM() {
		this.value = this.textContent.trim();
		this.textContent = '';
	}
}

customElements.define('u-textarea', Textarea);
